import React, { useState } from 'react';
import { XIcon, PlusIcon, TrashIcon, GripVerticalIcon, ChevronDownIcon, StarIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
interface CreateProcessusDialogProps {
  open: boolean;
  onClose: () => void;
}
type StepType = 'a-faire' | 'en-cours' | 'bloque' | 'termine';
interface Step {
  id: string;
  nomFr: string;
  nomEn: string;
  type: StepType;
}
// The four possible step states, each with its semantic colour.
const STEP_TYPES: {
  id: StepType;
  labelFr: string;
  labelEn: string;
  dot: string;
  chip: string;
  ring: string;
}[] = [{
  id: 'a-faire',
  labelFr: 'À faire',
  labelEn: 'To do',
  dot: 'bg-slate-400',
  chip: 'bg-slate-100 text-slate-700 border-slate-200',
  ring: 'ring-slate-400'
}, {
  id: 'en-cours',
  labelFr: 'En cours',
  labelEn: 'In progress',
  dot: 'bg-blue-500',
  chip: 'bg-blue-50 text-blue-700 border-blue-200',
  ring: 'ring-blue-500'
}, {
  id: 'bloque',
  labelFr: 'Bloqué',
  labelEn: 'Blocked',
  dot: 'bg-orange-500',
  chip: 'bg-orange-50 text-orange-700 border-orange-200',
  ring: 'ring-orange-500'
}, {
  id: 'termine',
  labelFr: 'Terminé',
  labelEn: 'Done',
  dot: 'bg-emerald-500',
  chip: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  ring: 'ring-emerald-500'
}];
const getStepType = (id: StepType) => STEP_TYPES.find(s => s.id === id) ?? STEP_TYPES[0];
export function CreateProcessusDialog({
  open,
  onClose
}: CreateProcessusDialogProps) {
  const {
    t
  } = useLanguage();
  const [dialogLang, setDialogLang] = useState<'fr' | 'en'>('fr');
  const [nomFr, setNomFr] = useState('');
  const [nomEn, setNomEn] = useState('');
  const [descFr, setDescFr] = useState('');
  const [descEn, setDescEn] = useState('');
  const [etapes, setEtapes] = useState<Step[]>([{
    id: '1',
    nomFr: 'Nouvelle demande',
    nomEn: 'New request',
    type: 'a-faire'
  }, {
    id: '2',
    nomFr: 'En analyse',
    nomEn: 'Under review',
    type: 'en-cours'
  }, {
    id: '3',
    nomFr: 'Émis',
    nomEn: 'Issued',
    type: 'termine'
  }]);
  // The step new requests start in by default.
  const [defaultStatusId, setDefaultStatusId] = useState('1');
  if (!open) return null;
  const addEtape = () => {
    setEtapes([...etapes, {
      id: Math.random().toString(),
      nomFr: 'Nouvelle étape',
      nomEn: 'New step',
      type: 'a-faire'
    }]);
  };
  const removeEtape = (id: string) => {
    const next = etapes.filter(e => e.id !== id);
    setEtapes(next);
    if (defaultStatusId === id) {
      setDefaultStatusId(next[0]?.id ?? '');
    }
  };
  const updateEtapeName = (id: string, value: string) => {
    setEtapes(etapes.map(e => e.id === id ? dialogLang === 'fr' ? {
      ...e,
      nomFr: value
    } : {
      ...e,
      nomEn: value
    } : e));
  };
  const updateEtapeType = (id: string, type: StepType) => {
    setEtapes(etapes.map(e => e.id === id ? {
      ...e,
      type
    } : e));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real app, this would save the new process
    onClose();
  };
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" role="dialog" aria-modal="true" data-id="element-1797" data-mp="3r1jnbwkpewij">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-[1px]" onClick={onClose} data-id="element-1798" data-mp="4ts69vaq8xgx" />
      <form onSubmit={handleSubmit} className="relative w-full max-w-2xl bg-white rounded-2xl border border-slate-200 shadow-xl max-h-[90vh] flex flex-col" data-id="element-1799" data-mp="3w03b82iuyidj">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200" data-id="element-1800" data-mp="1bax3sca6i2km">
          <h3 className="text-base font-bold text-[#0F172A]" data-id="element-1801" data-mp="3gz7m7o3dlf04">
            {t('Nouveau processus', 'New workflow')}
          </h3>
          <div className="flex items-center gap-3" data-id="element-1802" data-mp="3ly0xraz3qvxn">
            <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5" data-id="element-1803" data-mp="3qw2ie0c6f3b8">
              {(['fr', 'en'] as const).map(l => <button key={l} type="button" onClick={() => setDialogLang(l)} className={`px-2.5 py-1 rounded-md text-xs font-bold uppercase tracking-wide transition-colors ${dialogLang === l ? 'bg-[#0F172A] text-white shadow-sm' : 'text-slate-400 hover:text-slate-600'}`} data-id="element-1804" data-mp="3uzidgs8lkvlt">
                  {l}
                </button>)}
            </div>
            <button type="button" onClick={onClose} className="p-1.5 -mr-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors" data-id="element-1805" data-mp="minl46pfetlz">
              <XIcon className="w-5 h-5" data-id="element-1806" data-mp="2bzrffl2qjyko" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-6 overflow-y-auto" data-id="element-1807" data-mp="26zmzbw5qp5uz">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1808" data-mp="22yjib6byh3s6">
            <div className="sm:col-span-2" data-id="element-1809" data-mp="230xdrgajsi09">
              <label className="block text-sm font-semibold text-slate-700 mb-2" data-id="element-1810" data-mp="1y09r16tcqnbu">
                {t('Nom du processus', 'Workflow name')}{' '}
                <span className="text-xs font-medium text-slate-400" data-id="element-1811" data-mp="2up7hwdxze13q">
                  ({dialogLang === 'fr' ? 'FR' : 'EN'})
                </span>
              </label>
              <input type="text" value={dialogLang === 'fr' ? nomFr : nomEn} onChange={e => dialogLang === 'fr' ? setNomFr(e.target.value) : setNomEn(e.target.value)} placeholder={dialogLang === 'fr' ? 'Ex: Permis standard' : 'Ex: Standard permit'} className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5]" required={dialogLang === 'fr'} data-id="element-1812" data-mp="2lwnhp921jogs" />
            </div>
            <div className="sm:col-span-2" data-id="element-1813" data-mp="3ns07au6aau3n">
              <label className="block text-sm font-semibold text-slate-700 mb-2" data-id="element-1814" data-mp="3nqp4oxokrse4">
                {t('Description', 'Description')}{' '}
                <span className="text-xs font-medium text-slate-400" data-id="element-1815" data-mp="3esmx1zac61hv">
                  ({dialogLang === 'fr' ? 'FR' : 'EN'})
                </span>
              </label>
              <textarea value={dialogLang === 'fr' ? descFr : descEn} onChange={e => dialogLang === 'fr' ? setDescFr(e.target.value) : setDescEn(e.target.value)} rows={2} className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#4F46E5]" data-id="element-1816" data-mp="30uowbwoxu3ku" />
            </div>
          </div>

          {/* Steps */}
          <div data-id="element-1817" data-mp="2xa4z5spqhgdu">
            <div className="flex items-center justify-between mb-3" data-id="element-1818" data-mp="2xaoed1o01qo6">
              <label className="block text-sm font-semibold text-slate-700" data-id="element-1819" data-mp="2sjolq8lwxurh">
                {t('Étapes du processus', 'Workflow steps')}{' '}
                <span className="text-xs font-medium text-slate-400" data-id="element-1820" data-mp="3779lptnhrdv6">
                  ({dialogLang === 'fr' ? 'FR' : 'EN'})
                </span>
              </label>
              <button type="button" onClick={addEtape} className="text-xs font-medium text-[#4F46E5] hover:text-[#4338CA] flex items-center gap-1" data-id="element-1821" data-mp="3g9smm87pvges">
                <PlusIcon className="w-3.5 h-3.5" data-id="element-1822" data-mp="1lugb0y0qubbg" /> {t('Ajouter', 'Add')}
              </button>
            </div>

            <div className="space-y-2" data-id="element-1823" data-mp="1gx7z3f66v3in">
              {etapes.map(etape => {
              const type = getStepType(etape.type);
              return <div key={etape.id} className="flex flex-col sm:flex-row sm:items-center gap-3 p-2.5 border border-slate-200 rounded-lg bg-slate-50 group" data-id="element-1824" data-mp="101u49s0talk8">
                    <GripVerticalIcon className="hidden sm:block w-4 h-4 text-slate-400 cursor-grab flex-shrink-0" data-id="element-1825" data-mp="19s59dhw8f0sp" />
                    <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${type.dot}`} aria-hidden="true" data-id="element-1826" data-mp="d2o43415vgrj" />
                    <input type="text" value={dialogLang === 'fr' ? etape.nomFr : etape.nomEn} onChange={e => updateEtapeName(etape.id, e.target.value)} placeholder={dialogLang === 'fr' ? "Nom de l'étape" : 'Step name'} className="flex-1 min-w-0 px-2 py-1.5 text-sm border border-transparent hover:border-slate-200 focus:border-slate-300 rounded bg-transparent focus:bg-white focus:outline-none" data-id="element-1827" data-mp="m1rpwfrpqa9q" />
                    {/* Default badge */}
                    {defaultStatusId === etape.id && <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold bg-indigo-50 text-[#4F46E5] border border-indigo-100 flex-shrink-0" data-id="element-1828" data-mp="388xmvr2511la">
                        <StarIcon className="w-3 h-3 fill-current" data-id="element-1829" data-mp="2z8rbqbtwwdpa" />
                        {t('Défaut', 'Default')}
                      </span>}
                    {/* Set-as-default hover action */}
                    {defaultStatusId !== etape.id && <button type="button" onClick={() => setDefaultStatusId(etape.id)} className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-slate-400 hover:text-[#4F46E5] hover:bg-white border border-transparent hover:border-slate-200 transition-all opacity-0 group-hover:opacity-100 focus:opacity-100 flex-shrink-0" data-id="element-1830" data-mp="sib2y5npa5mu">
                        <StarIcon className="w-3 h-3" data-id="element-1831" data-mp="szflp11tgf6j" />
                        {t('Définir par défaut', 'Set as default')}
                      </button>}
                    {/* Step type selector */}
                    <div className="relative flex-shrink-0" data-id="element-1832" data-mp="8xi7yx6kbsl">
                      <select value={etape.type} onChange={e => updateEtapeType(etape.id, e.target.value as StepType)} aria-label={t('Type d’étape', 'Step type')} className={`appearance-none pl-6 pr-7 py-1.5 rounded-md text-xs font-medium border bg-white focus:outline-none focus:ring-2 focus:ring-[#4F46E5] cursor-pointer ${type.chip}`} data-id="element-1833" data-mp="3riw5sa97wyp5">
                        {STEP_TYPES.map(st => <option key={st.id} value={st.id} data-id="element-1834" data-mp="1szhpsfva38l7">
                            {dialogLang === 'fr' ? st.labelFr : st.labelEn}
                          </option>)}
                      </select>
                      <span className={`pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full ${type.dot}`} aria-hidden="true" data-id="element-1835" data-mp="1jazntvb9ivfz" />
                      <ChevronDownIcon className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 opacity-60" data-id="element-1836" data-mp="2bxu6qnsflmrc" />
                    </div>
                    <button type="button" onClick={() => removeEtape(etape.id)} disabled={etapes.length <= 1} className="p-1.5 text-slate-400 hover:text-red-600 rounded disabled:opacity-30 disabled:hover:text-slate-400 flex-shrink-0 self-end sm:self-auto" aria-label={t('Supprimer l\u2019\u00e9tape', 'Remove step')} data-id="element-1837" data-mp="21zegso554jjf">
                      <TrashIcon className="w-4 h-4" data-id="element-1838" data-mp="2jgk1b96l3d6l" />
                    </button>
                  </div>;
            })}
            </div>
            <p className="mt-2 text-xs text-slate-400" data-id="element-1839" data-mp="2ahzmij459nnn">
              {t('Survolez une étape pour la définir comme statut par défaut des nouvelles demandes.', 'Hover a step to set it as the default status for new requests.')}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-2xl" data-id="element-1840" data-mp="1vlqr5sndjugi">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 border border-slate-200 bg-white rounded-lg hover:bg-slate-50 transition-colors" data-id="element-1841" data-mp="3uuxjpyikj0f8">
            {t('Annuler', 'Cancel')}
          </button>
          <button type="submit" disabled={!nomFr.trim()} className="px-4 py-2 text-sm font-medium text-white bg-[#4F46E5] rounded-lg hover:bg-[#4338CA] transition-colors disabled:opacity-50" data-id="element-1842" data-mp="69bzvdgvnqc5">
            {t('Créer le processus', 'Create workflow')}
          </button>
        </div>
      </form>
    </div>;
}